import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import { canAddCommentsFromOperations } from '@confluence/entity-operations/entry-points/canAddCommentsFromOperations.ts';
import { ContentOperationsFragment } from '@confluence/entity-operations/entry-points/ContentOperationsFragment.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	useAddCommentPermissionCheckPermissionCheckAddCommentQuery as useAddCommentPermissionCheckPermissionCheckAddCommentQuery$data,
	useAddCommentPermissionCheckPermissionCheckAddCommentQueryVariables as useAddCommentPermissionCheckPermissionCheckAddCommentQuery$variables,
} from './__types__/useAddCommentPermissionCheckPermissionCheckAddCommentQuery.ts';

void ContentOperationsFragment;

const useAddCommentPermissionCheckPermissionCheckAddCommentQuery = graphql`
	query useAddCommentPermissionCheckPermissionCheckAddCommentQuery(
		$contentId: ID!
		$status: [String]
	) {
		content(id: $contentId, status: $status) {
			nodes {
				...ContentOperationsFragment @relay(mask: false)
			}
		}
	}
`;

type RefetchFunction = () => Promise<unknown>;

const noOpRefetch: RefetchFunction = () => Promise.resolve();

export const useAddCommentPermissionCheck = (
	contentId: string,
	// Retained for callers that pass preloaded permissions as the third argument.
	_cacheOnly = false,
	preloadedCanAddComments?: boolean,
): {
	canAddComments: boolean;
	loading: boolean;
	contentQueryError: ApolloErrorInterop | undefined;
	refetch: RefetchFunction;
	networkStatus: NetworkStatus;
} => {
	const baseOptions = {
		skip: !contentId || preloadedCanAddComments !== undefined,
	};
	const status = ['draft', 'current'];

	const { data, loading, error, networkStatus } = useQueryNoFurtherUpdateInterop<
		useAddCommentPermissionCheckPermissionCheckAddCommentQuery$data,
		useAddCommentPermissionCheckPermissionCheckAddCommentQuery$variables
	>(useAddCommentPermissionCheckPermissionCheckAddCommentQuery, {
		...baseOptions,
		variables: {
			contentId,
			status,
		},
		skipFetchIfCached: false,
	});

	const createPermission = canAddCommentsFromOperations(data?.content?.nodes?.[0]?.operations);

	return {
		canAddComments: preloadedCanAddComments ?? createPermission,
		loading: preloadedCanAddComments === undefined ? loading : false,
		contentQueryError: preloadedCanAddComments === undefined ? error : undefined,
		refetch: noOpRefetch,
		networkStatus,
	};
};
